/**
 * §9.13 Billing tab — the live panel (FE4 / ENG-5450).
 *
 * Fetches `ENGAGEMENT_BILLING` on mount, which happens only when the tab is
 * opened: `connectPayoutsEnabled` is a live Stripe read on the api and stays
 * off the Hub's hot path by design (see the query's doc in `operations.ts`).
 * The Hub renders only the current tab's node, so this panel unmounts and
 * remounts on every tab visit — `cache-first` is what actually keeps that live
 * read to the first open per session (a `nextFetchPolicy` would be reset by the
 * remount). Charges accrue on a cadence, not second-by-second; the Retry
 * button is the in-session refresh.
 *
 * The viewer's side arrives resolved: `billing.customer` XOR `billing.expert`,
 * whole objects, wrong side null. The panel renders the arm the SERVER named in
 * `viewerSide` — never the client's role model, and never merely whichever
 * object happens to be non-null, so a server regression that returned both arms
 * still cannot show an expert the customer's charges.
 *
 * **The OF-staff view toggle** (§11.2 #8/#9, ENG-5428's `viewerSide` arg) is
 * defense in depth on both sides: the control renders only for internal staff,
 * AND the argument is only ever sent for them — the api hard-rejects a
 * non-staff caller passing it rather than downgrading. Staff with no choice
 * made send no argument and get the server's default (customer view). The
 * active side is read back off the RESPONSE's `viewerSide`, the server's
 * resolution, never the local click state — and the toggle outlives the
 * loading and error states so a failed flip is never a one-way door.
 */
import { useQuery } from '@apollo/client';
import { useState } from 'react';

import { EngagementBillingViewerSide } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import { hasErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';

import type { SessionViewer } from '../../components/EngagementHub/roles';
import { ENGAGEMENT_BILLING } from '../operations';
import type { EngagementView } from '../viewModel';
import { fromEngagementBilling } from '../viewModel';
import CustomerBillingArm from './CustomerBillingArm';
import ExpertBillingArm from './ExpertBillingArm';

interface BillingPanelProps {
  view: EngagementView;
  viewer: SessionViewer;
}

interface StaffViewToggleProps {
  active: EngagementBillingViewerSide | null;
  busy: boolean;
  onSelect: (side: EngagementBillingViewerSide) => void;
}

function StaffViewToggle({ active, busy, onSelect }: StaffViewToggleProps): JSX.Element {
  const option = (side: EngagementBillingViewerSide, label: string) => (
    <Button
      size="small"
      variant={active === side ? 'primary' : 'secondary'}
      aria-pressed={active === side}
      disabled={busy}
      onClick={() => onSelect(side)}
    >
      {label}
    </Button>
  );
  return (
    <div className="flex flex-wrap items-center gap-8">
      <span className="text-on-surface-muted subtitle-2">Viewing as</span>
      {option(EngagementBillingViewerSide.Customer, 'Customer')}
      {option(EngagementBillingViewerSide.Expert, 'Expert')}
    </div>
  );
}

const Quiet = ({ children }: { children: React.ReactNode }) => (
  <p className="text-on-surface-muted body-2">{children}</p>
);

export default function BillingPanel({ view, viewer }: BillingPanelProps): JSX.Element {
  const [staffSide, setStaffSide] = useState<EngagementBillingViewerSide | null>(null);
  const { data, loading, error, refetch } = useQuery(ENGAGEMENT_BILLING, {
    variables: {
      id: view.id,
      // Never sent for a non-staff viewer — the server rejects it outright.
      ...(viewer.isOfStaff && staffSide ? { viewerSide: staffSide } : {}),
    },
    fetchPolicy: 'cache-first',
    // `connectPayoutsEnabled` is a live Stripe read the api nulls on an
    // outage — a field-level error must degrade to that tri-state null, not
    // discard the whole response the way the default 'none' policy would.
    errorPolicy: 'all',
    // So Retry and a staff side-flip both report themselves as in flight.
    notifyOnNetworkStatusChange: true,
  });

  const billing = data?.engagement?.billing ?? null;
  const tab = billing ? fromEngagementBilling(billing) : null;

  // Whatever the fetch is doing, staff keep their way back to the other side:
  // an error or a slow flip must never stand between them and the toggle.
  const withToggle = (body: JSX.Element): JSX.Element => {
    if (!viewer.isOfStaff) return body;
    return (
      <div className="flex flex-col gap-16">
        <StaffViewToggle
          // The server's resolved side, never the click. Until the first
          // response lands there is no resolved side, so neither reads pressed.
          active={tab?.viewerSide ?? null}
          busy={loading}
          onSelect={setStaffSide}
        />
        {body}
      </div>
    );
  };

  if (loading && !data) {
    return withToggle(
      <p className="text-on-surface-muted body-2" role="status">
        Loading billing…
      </p>
    );
  }

  if (error && !data) {
    // A missing — or not-permitted — engagement comes back as `engagement:
    // null` plus `engagement.not_found`, and so does the api's rejection of a
    // `viewerSide` it won't honour. Neither is retryable, so both fall through
    // to the quiet copy below rather than offering a button that can't work
    // (the `EngagementPage` precedent).
    if (!hasErrorCode(error, ERROR_CODES.ENGAGEMENT_NOT_FOUND)) {
      return withToggle(
        <div className="rounded-lg border border-surface-border bg-surface-card p-24">
          <h2 className="text-on-surface hd-6">We couldn’t load billing.</h2>
          <p className="mt-8 text-on-surface-muted body-2">
            The connection may have dropped. Try again in a moment.
          </p>
          <div className="mt-16">
            <Button disabled={loading} onClick={() => void refetch().catch(() => {})}>
              Retry
            </Button>
          </div>
        </div>
      );
    }
  }

  if (!tab) {
    // Reachable only off the happy path (e.g. activity flipped false between
    // the Hub read and this one). Quiet and honest, not an error.
    return withToggle(<Quiet>No billing activity yet.</Quiet>);
  }

  // The arm the server resolved, not the arm that happens to be populated.
  // On a contradiction (side named but its arm null, or the wrong arm sent)
  // we show nothing rather than the other side's money.
  const arm =
    tab.viewerSide === EngagementBillingViewerSide.Customer && tab.customer ? (
      <CustomerBillingArm arm={tab.customer} view={view} />
    ) : tab.viewerSide === EngagementBillingViewerSide.Expert && tab.expert ? (
      <ExpertBillingArm arm={tab.expert} view={view} />
    ) : (
      <Quiet>No billing activity yet.</Quiet>
    );

  return withToggle(arm);
}
